import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { useAuth } from "../../../context/AuthContext";
import "./chatIA.scss";

// ── Tipos ────────────────────────────────────────────────────────────────────
interface Mensaje {
  rol: "usuario" | "asistente";
  contenido: string;
}

const API_URL = process.env.REACT_APP_API_URL ?? "http://localhost:4020";

const MENSAJE_BIENVENIDA: Mensaje = {
  rol: "asistente",
  contenido:
    "¡Hola! Soy el asistente IA del SIF. Puedo consultarte información en tiempo real sobre ventas, inventario, clientes, compras y más. ¿En qué te puedo ayudar?",
};

const SUGERENCIAS = [
  "¿Cuánto vendimos hoy?",
  "¿Qué artículos tienen stock crítico?",
  "¿Cuántos clientes nuevos hay este mes?",
  "¿Cuántas compras hay pendientes?",
];

function sessionKey(userId: number): string {
  return `chatia_historial_${userId}`;
}

function cargarHistorial(userId: number): Mensaje[] {
  try {
    const guardado = sessionStorage.getItem(sessionKey(userId));
    if (guardado) return JSON.parse(guardado) as Mensaje[];
  } catch {}
  return [MENSAJE_BIENVENIDA];
}

// ── Componente ────────────────────────────────────────────────────────────────
const ChatIA = () => {
  const { token, user } = useAuth();
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState<Mensaje[]>(() => cargarHistorial(user?.Id ?? 0));
  const [input, setInput] = useState("");
  const [cargando, setCargando] = useState(false);
  const [pulso, setPulso] = useState(true);

  const mensajesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const habilitado =
    !!user?.AgenteIA &&
    user?.Perfil?.toLowerCase() === 'administrador';

  // Persistir historial en sessionStorage al cambiar mensajes
  useEffect(() => {
    try {
      sessionStorage.setItem(sessionKey(user?.Id ?? 0), JSON.stringify(mensajes));
    } catch {}
  }, [mensajes]);

  // Scroll automático al último mensaje
  useEffect(() => {
    mensajesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensajes, cargando]);

  // Quitar pulso al abrir por primera vez
  useEffect(() => {
    if (abierto) setPulso(false);
  }, [abierto]);

  // Foco en input al abrir
  useEffect(() => {
    if (abierto) setTimeout(() => inputRef.current?.focus(), 100);
  }, [abierto]);

  if (!habilitado) return null;

  const enviar = async (pregunta?: string) => {
    const texto = (pregunta ?? input).trim();
    if (!texto || cargando) return;

    const nuevosMensajes: Mensaje[] = [
      ...mensajes,
      { rol: "usuario", contenido: texto },
    ];
    setMensajes(nuevosMensajes);
    setInput("");
    setCargando(true);

    try {
      const historial = nuevosMensajes.slice(-7, -1).map((m) => ({
        rol: m.rol,
        contenido: m.contenido,
      }));

      const res = await fetch(`${API_URL}/agente-ia/consultar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ pregunta: texto, historial }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setMensajes((prev) => [
        ...prev,
        { rol: "asistente", contenido: data.respuesta },
      ]);
    } catch {
      setMensajes((prev) => [
        ...prev,
        {
          rol: "asistente",
          contenido:
            "Lo siento, no pude obtener una respuesta en este momento. Verifica que el servicio de IA esté activo.",
        },
      ]);
    } finally {
      setCargando(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const limpiarChat = () => {
    const reinicio: Mensaje[] = [{ rol: "asistente", contenido: "Chat reiniciado. ¿En qué te puedo ayudar?" }];
    sessionStorage.setItem(sessionKey(user?.Id ?? 0), JSON.stringify(reinicio));
    setMensajes(reinicio);
  };

  return (
    <>
      {/* ── Panel de chat ──────────────────────────────────────────────── */}
      {abierto && (
        <div className="chatia-panel">
          {/* Header */}
          <div className="chatia-header">
            <div className="chatia-header-info">
              <span className="chatia-avatar">🤖</span>
              <div>
                <span className="chatia-title">Asistente SIF</span>
                <span className="chatia-status">
                  <span className="chatia-dot" />
                  En línea
                </span>
              </div>
            </div>
            <div className="chatia-header-actions">
              <button
                className="chatia-icon-btn"
                title="Limpiar chat"
                onClick={limpiarChat}
              >
                🗑
              </button>
              <button
                className="chatia-icon-btn"
                title="Cerrar"
                onClick={() => setAbierto(false)}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Mensajes */}
          <div className="chatia-mensajes">
            {mensajes.map((m, i) => (
              <div
                key={i}
                className={`chatia-burbuja chatia-burbuja--${m.rol}`}
              >
                {m.rol === "asistente" && (
                  <span className="chatia-burbuja-avatar">🤖</span>
                )}
                <div className="chatia-burbuja-texto">
                  {m.contenido.split("\n").map((line, j) => (
                    <span key={j}>
                      {line}
                      {j < m.contenido.split("\n").length - 1 && <br />}
                    </span>
                  ))}
                </div>
              </div>
            ))}

            {/* Indicador de escritura */}
            {cargando && (
              <div className="chatia-burbuja chatia-burbuja--asistente">
                <span className="chatia-burbuja-avatar">🤖</span>
                <div className="chatia-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={mensajesEndRef} />
          </div>

          {/* Sugerencias (solo al inicio) */}
          {mensajes.length === 1 && (
            <div className="chatia-sugerencias">
              {SUGERENCIAS.map((s) => (
                <button
                  key={s}
                  className="chatia-sugerencia"
                  onClick={() => enviar(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="chatia-input-area">
            <textarea
              ref={inputRef}
              className="chatia-input"
              placeholder="Escribe tu pregunta... (Enter para enviar)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              disabled={cargando}
            />
            <button
              className="chatia-send-btn"
              onClick={() => enviar()}
              disabled={!input.trim() || cargando}
              title="Enviar"
            >
              ➤
            </button>
          </div>
        </div>
      )}

      {/* ── Botón flotante ─────────────────────────────────────────────── */}
      <button
        className={`chatia-fab ${pulso ? "chatia-fab--pulso" : ""} ${abierto ? "chatia-fab--activo" : ""}`}
        onClick={() => setAbierto((v) => !v)}
        title="Asistente IA"
      >
        {abierto ? "✕" : "🤖"}
      </button>
    </>
  );
};

export default ChatIA;
